/**
 * The instance catalogue: what each provider sells, and what a node of each size
 * needs to know to work out its Kubernetes limits.
 *
 * The data is a committed snapshot written by `scripts/update-catalog.mjs`. Each
 * provider is its own JSON chunk, loaded on demand, so a visitor who only looks at
 * AWS never downloads Azure's price list.
 */

export const PROVIDERS = ['aws', 'gcp', 'azure', 'oci'] as const
export type ProviderId = (typeof PROVIDERS)[number]

export const CATEGORIES = ['general', 'compute', 'memory', 'storage'] as const
export type Category = (typeof CATEGORIES)[number]

export function isProviderId(value: unknown): value is ProviderId {
  return PROVIDERS.includes(value as ProviderId)
}

export interface Family {
  readonly id: string
  readonly category: Category
  /** Shown before the visitor touches the filter: current, mainstream families. */
  readonly default: boolean
}

export interface Instance {
  readonly provider: ProviderId
  readonly name: string
  readonly family: string
  readonly vcpu: number
  readonly memoryGiB: number
  /** On-demand Linux list price in USD, in the catalogue's reference region. */
  readonly hourlyPrice: number
  /** Persistent volumes the node's CSI driver lets you attach; `null` if unpublished. */
  readonly maxVolumes: number | null
  readonly arm: boolean
  /** Burstable or shared-core: CPU beyond a baseline is credit-limited. */
  readonly burstable: boolean
  /** AWS: network interfaces and IPv4 addresses per interface, for the VPC CNI. */
  readonly enis?: number
  readonly ipv4PerEni?: number
  /** AWS: Nitro hypervisor — the only kind that supports prefix delegation. */
  readonly nitro?: boolean
  /** GCP: e2-micro, e2-small, e2-medium. */
  readonly sharedCore?: boolean
  /** OCI: the shape's OCPU count and the VNICs it can attach. */
  readonly ocpus?: number
  readonly vnics?: number
}

export interface Catalog {
  readonly provider: ProviderId
  /** Where the prices come from; `all` when the provider prices every region alike. */
  readonly region: string
  readonly retrieved: string
  readonly families: readonly Family[]
  readonly instances: readonly Instance[]
}

/** `aws:m7i.xlarge` — unique across providers, and what share links carry. */
export function instanceKey(instance: Pick<Instance, 'provider' | 'name'>): string {
  return `${instance.provider}:${instance.name}`
}

export function parseInstanceKey(key: string): { provider: ProviderId; name: string } | null {
  const separator = key.indexOf(':')
  const provider = key.slice(0, separator)
  const name = key.slice(separator + 1)
  return separator > 0 && isProviderId(provider) && name !== '' ? { provider, name } : null
}

type RawCatalog = Omit<Catalog, 'provider' | 'instances'> & {
  readonly instances: readonly Omit<Instance, 'provider'>[]
}

const LOADERS: Record<ProviderId, () => Promise<{ default: RawCatalog }>> = {
  aws: () => import('../catalog/aws.json') as Promise<{ default: RawCatalog }>,
  gcp: () => import('../catalog/gcp.json') as Promise<{ default: RawCatalog }>,
  azure: () => import('../catalog/azure.json') as Promise<{ default: RawCatalog }>,
  oci: () => import('../catalog/oci.json') as Promise<{ default: RawCatalog }>,
}

/** Stamps the provider onto every instance, so an instance stands on its own. */
export function toCatalog(provider: ProviderId, raw: RawCatalog): Catalog {
  return {
    ...raw,
    provider,
    instances: raw.instances.map((instance) => ({ ...instance, provider })),
  }
}

const cache = new Map<ProviderId, Promise<Catalog>>()

export function loadCatalog(provider: ProviderId): Promise<Catalog> {
  let pending = cache.get(provider)
  if (!pending) {
    pending = LOADERS[provider]().then((module) => toCatalog(provider, module.default))
    // A failed load (offline before the chunk was cached) must be retryable.
    pending.catch(() => cache.delete(provider))
    cache.set(provider, pending)
  }
  return pending
}
